/**
 * 그룹 수정 모달 컴포넌트
 * - 그룹명, 설명 수정
 * - is_active 수정 (admin만)
 * - 코드(code)는 수정 불가 (disabled)
 */

import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Switch, message } from 'antd';
import { groupService } from '../../../services/groupService';
import { useAuthStore } from '../../../store/authStore';
import type { Group, GroupUpdateRequest } from '../../../types/group';

const { TextArea } = Input;

interface GroupEditModalProps {
  open: boolean;
  group: Group | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const GroupEditModal: React.FC<GroupEditModalProps> = ({
  open,
  group,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { user: currentUser } = useAuthStore();

  /**
   * 그룹 정보가 변경되면 폼에 반영
   */
  useEffect(() => {
    if (group && open) {
      form.setFieldsValue({
        name: group.name,
        description: group.description,
        is_active: group.is_active,
      });
    }
  }, [group, open, form]);

  /**
   * 모달 닫기 처리
   */
  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  /**
   * 그룹 수정 제출
   */
  const handleSubmit = async () => {
    if (!group) return;

    try {
      const values = await form.validateFields();
      setLoading(true);

      const data: GroupUpdateRequest = {
        name: values.name,
        description: values.description || undefined,
      };

      // admin만 is_active 수정 가능
      if (currentUser?.role === 'admin') {
        data.is_active = values.is_active;
      }

      await groupService.updateGroup(group.id, data);
      message.success('그룹 정보가 수정되었습니다');
      handleClose();
      onSuccess();
    } catch (error: any) {
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error('그룹 수정 실패');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="그룹 정보 수정"
      open={open}
      onOk={handleSubmit}
      onCancel={handleClose}
      confirmLoading={loading}
      okText="저장"
      cancelText="취소"
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
      >
        <Form.Item
          label="그룹명"
          name="name"
          rules={[
            { required: true, message: '그룹명을 입력하세요' },
            { min: 1, max: 100, message: '그룹명은 1-100자여야 합니다' },
          ]}
        >
          <Input placeholder="예: 강남학원" />
        </Form.Item>

        <Form.Item label="그룹 코드 (수정 불가)">
          <Input value={group?.code} disabled />
        </Form.Item>

        <Form.Item
          label="설명"
          name="description"
          rules={[{ max: 500, message: '설명은 500자 이하여야 합니다' }]}
        >
          <TextArea
            rows={3}
            placeholder="그룹 설명을 입력하세요 (선택사항)"
          />
        </Form.Item>

        {/* admin만 활성화 상태 수정 가능 */}
        {currentUser?.role === 'admin' && (
          <Form.Item
            label="활성화 상태"
            name="is_active"
            valuePropName="checked"
          >
            <Switch checkedChildren="활성" unCheckedChildren="비활성" />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};
